import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import AutoCompleteUIDTO, { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import { traceo } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import useLocalStorage, { STORAGE_KEYS } from "./useLocalStorage";
import ProjectService from "../services/ProjectService";
import { Routes } from "../types/Routes";
import { AssignationStatusFilters } from "../utils/enums/filters/AssignationStatusFilters";
import useUpdateUrl from "./useUpdateUrl";
import { ProjectTimelineEntry, ProjectTimelinePosition } from "../types/ProjectTimelineTypes";
import PositionService from "../services/PositionService";
import RoleUtils from "../utils/RoleUtils";
import AuthService from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/AuthService";
import { PositionStatusFilters } from "../utils/enums/filters/PositionStatusFilters";

const useProjectTimeline = () => {
  const [projectsNames, setProjectNames] = useState<AutoCompleteUIDTO[]>([]);
  const [statusFilter, setStatusFilter] = useState<string>(AssignationStatusFilters.ACTIVE);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO | undefined>(undefined);
  const [userIsManager, setUserIsManager] = useState(false);
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);
  const [rows, setRows] = useState<ProjectTimelineEntry[]>([]);
  const [positionNames, setPositionNames] = useState<ProjectTimelinePosition[]>([]);
  const [positionStatusFilter, setPositionStatusFilter] = useState<string>(PositionStatusFilters.ALL);

  const searchParams = new URLSearchParams(useLocation().search);
  const project = searchParams.get("project");
  const status = searchParams.get("status");
  const { updateUrl } = useUpdateUrl(Routes.PROJECTS_TIMELINE, searchParams);

  useEffect(() => {
    getAllProjectsNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (project !== null && projectsNames) {
      const projectFilter = projectsNames.find((p) => p.externalId === project);
      if (projectFilter) setSelectedProject(projectFilter);
    } else if (storedProject !== null && projectsNames) {
      const projectFilter = projectsNames.find((p) => p.externalId === storedProject.externalId);
      if (projectFilter) setSelectedProject(projectFilter);
      searchParams.set("project", storedProject.externalId);
    }

    if (status !== null) {
      setStatusFilter(status);
    } else {
      searchParams.set("status", AssignationStatusFilters.ALL);
    }
    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsNames]);

  useEffect(() => {
    if (selectedProject) {
      getAllPositionNames();
      getAllRows(selectedProject.externalId);
      checkIfUserIsManager();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  /**
   * Performs asynchronous data fetching and error handling.
   *
   * @param fetchFunction A function that returns a Promise of type T.
   * @param errorMessage A message to display in case of an error.
   * @returns A Promise of type T, representing the fetched data.
   */
  async function handleFetchData<T>(fetchFunction: () => Promise<T>, errorMessage: string): Promise<T> {
    setIsLoading(true);

    try {
      return await fetchFunction();
    } catch (error) {
      traceo(errorMessage, error, null);
      return Promise.reject(error);
    } finally {
      setIsLoading(false);
    }
  }

  const checkIfUserIsManager = async () => {
    if (RoleUtils.isAdmin()) {
      setUserIsManager(true);
      return;
    }

    const isManager = (
      await ProjectService.isUserCoreManagerFromProject(selectedProject!.externalId!, AuthService.getUser().externalId!)
    ).succeeded();
    setUserIsManager(isManager);
  };

  const getAllProjectsNames = async (assigned: boolean = false) => {
    const fetchProjectsNamesData = async () => {
      return (await ProjectService.findAllProjectsNames(false, assigned)).getContent();
    };

    setProjectNames(await handleFetchData(fetchProjectsNamesData, "Error getting project names"));
  };

  const getAllPositionNames = async () => {
    const fetchPositionNamesData = async () => {
      const response = await PositionService.findByProjectExternalIdAndRoleKey(0, 50, selectedProject!.externalId, "All", true);
      return response.getContent().content.map((position) => ({
        externalId: position.externalId!,
        externalName: position.externalRoleName,
        internalName: position.internalRoleName,
        startDate: new Date(position.shortStartDate),
        endDate: position.shortEndDate ? new Date(position.shortEndDate) : undefined,
      }));
    };

    setPositionNames(await handleFetchData(fetchPositionNamesData, "Error getting position names"));
  };

  const getAllRows = async (externalId: string) => {
    const fetchRowsData = async () => {
      return (await ProjectService.getTimeline(externalId)).getContentOrThrowError();
    };

    setRows(await handleFetchData(fetchRowsData, "Error getting rows"));
  };

  const handleSelectProject = (project: AutoCompleteUIDTO) => {
    setSelectedProject(project ?? EMPTY_AUTOCOMPLETE);
    setStoredProject(project ?? EMPTY_AUTOCOMPLETE);
    if (project?.externalId) {
      searchParams.set("project", project.externalId);
    } else {
      searchParams.delete("project");
    }
    updateUrl();
  };

  const onAssignationUpdate = (updated: boolean) => {
    if (selectedProject && updated) {
      getAllRows(selectedProject.externalId);
    }
  };

  const fetchPositions = () => {
    getAllProjectsNames();
  };

  return {
    userIsManager,
    projectsNames,
    positionNames,
    handleSelectProject,
    statusFilter,
    setStatusFilter,
    isLoading,
    selectedProject,
    setSelectedProject,
    rows,
    onAssignationUpdate,
    fetchPositions,
    positionStatusFilter,
    setPositionStatusFilter,
  };
};

export default useProjectTimeline;
